import { useCallback, useRef, useState } from "react";
import editIcon from "../../../../../../assets/image/reasonEditIcon.png";
import editHoverIcon from "../../../../../../assets/image/reasonEditHoverIcon.png";
import NormalToolTip from "../../../../../../base/basicComponents/toolTip";
import "../index.scss";
import applyPMPExcludedService from "../../../../../../service/applyPmpExcluded";
import { useClickAway, useEventListener, useRequest, useSetState } from "ahooks";
import procedureListService from "service/procedureList";

import { PMPExcludeEditFieldIconProps } from "../types";
import { wrapRequestToPromise } from "utils/index";

import { message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { debounce } from "lodash-es";

export default function PMPExcludedEditIcon(props: PMPExcludeEditFieldIconProps) {
  const [currentEditIcon, setCurrentEditIcon] = useState(editIcon);
  const editIconRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useSetState({
    showToolTop: false,
    toolTipContent: "",
  });

  const { run: runGetSectionLock, loading: loading1 } = useRequest(
    procedureListService.checkSectionLock,
    {
      manual: true,
      onBefore: () => {
        setTooltip({
          showToolTop: false,
        });
        handleEditIconDefault();
      },
      onSuccess: (result) => {
        setTooltip({
          showToolTop: result.data.isLocked,
          toolTipContent: `${result.data.lockUser} is editing this section.`,
        });
        if (!result.data.isLocked) {
          runCheckApplyPmpExcludedEdit({
            procedureTaskId: props.procedureTaskId,
            procedureId: props.procedureId,
            procedureAccountId: props.procedureAccountId,
            IsExcluded: props.pmpExcluded,
          });
        }
      },
      onError: () => {
        message.error("Failed to open PMP Excluded Edit Modal");
      },
    }
  );

  const { run: runCloseSectionLock } = useRequest(procedureListService.setSectionLock, {
    manual: true,
  });

  const { run: runCheckApplyPmpExcludedEdit, loading: loading2 } = useRequest(
    wrapRequestToPromise(applyPMPExcludedService.checkApplyPmpExcludedEdit),
    {
      manual: true,
      onSuccess: (res) => {
        if (!res.result && res.messageCode === 16) {
          setTooltip({
            showToolTop: true,
            toolTipContent: "Apply for PMP Excluded Process is pending approval.",
          });
          // unlock the section when not open the modal
          runCloseSectionLock({
            procedureId: props.uniqueKey,
            procedureSectionType: "PMPExcluded",
            setLock: false,
          });
        } else if (props.pmpExcluded !== res.isExcluded || res.messageCode === 29) {
          props.refreshProcedureList();
          message.warning(
            "The PMP Excluded has been updated by another user. The latest data has been loaded to ensure accuracy.",
            4
          );
          // unlock the section when not open the modal
          runCloseSectionLock({
            procedureId: props.uniqueKey,
            procedureSectionType: "PMPExcluded",
            setLock: false,
          });
        } else {
          setTooltip({
            showToolTop: false,
          });
          props.handleModalOpen();
        }
      },
      onError: () => {
        message.error("Failed to open PMP Excluded Edit Modal");
      },
    }
  );

  /**
   * function about change icon to hover icon when hovering edit icon
   */
  const handleEditIconHover = () => {
    setCurrentEditIcon(editHoverIcon);
  };

  /**
   * function about change icon to default icon when leaving edit icon
   */
  const handleEditIconDefault = () => {
    setCurrentEditIcon(editIcon);
  };

  /**
   * Handles the edit icon click event by initiating a section lock request
   * for the PMP Excluded procedure section with a 20-minute timeout.
   */
  const handleEditClick = () => {
    runGetSectionLock({
      procedureId: props.uniqueKey,
      procedureSectionType: "PMPExcluded",
      timeOutMins: 20,
    });
  };
  const hideTooltip = useCallback(
    debounce(
      () => {
        setTooltip({ showToolTop: false });
      },
      100,
      {
        leading: true,
        trailing: false,
      }
    ),
    [setTooltip]
  );

  useClickAway(hideTooltip, editIconRef, ["click", "pointerdown"]);
  useEventListener("scroll", hideTooltip, { target: window, capture: true });

  const loading = loading1 || loading2 || props.isEditDisabled;
  return (
    <NormalToolTip
      element={
        loading ? (
          <LoadingOutlined className="ml-1" />
        ) : (
          <div ref={editIconRef} className={props?.disabled ? "setCursor" : ""}>
            <div
              tabIndex={-1}
              onMouseEnter={handleEditIconHover}
              onMouseLeave={handleEditIconDefault}
              onClick={handleEditClick}
              className={`editIcon ${props?.disabled && "pmpExcludedDisable"}`}
              data-testid="pmp-excluded-edit-icon-btn"
            >
              <img src={currentEditIcon} alt="edit icon" />
            </div>
          </div>
        )
      }
      title={tooltip.toolTipContent}
      open={tooltip.showToolTop}
      placement="right"
    />
  );
}
